import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TenantContextResponse, TenantSummary } from '../types/tenantSwitcher';

interface TenantSelectorProps {
  context: TenantContextResponse | undefined;
  tenants: TenantSummary[] | undefined;
  isLoading: boolean;
  isSwitching: boolean;
  hasSwitchPermission: boolean;
  onSwitch: (tenantId: string) => void;
}

export function TenantSelector({
  context,
  tenants,
  isLoading,
  isSwitching,
  hasSwitchPermission,
  onSwitch,
}: TenantSelectorProps) {
  const { t } = useTranslation();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const val = e.target.value;
      if (val && val !== context?.currentTenant) {
        onSwitch(val);
      }
    },
    [context?.currentTenant, onSwitch]
  );

  const resolvedFromKey = context?.resolvedFrom
    ? `tenantSwitcher.selector.resolvedFrom.${context.resolvedFrom}`
    : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-4)',
        flexWrap: 'wrap',
      }}
    >
      {/* 라벨 */}
      <label
        htmlFor="tenant-selector"
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          whiteSpace: 'nowrap',
        }}
      >
        {t('tenantSwitcher.selector.label')}
      </label>

      {/* 셀렉트 */}
      {isLoading ? (
        <div
          style={{
            width: 200,
            height: 36,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-elevated)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
          aria-label={t('tenantSwitcher.selector.placeholder')}
        />
      ) : (
        <select
          id="tenant-selector"
          value={context?.currentTenant ?? ''}
          onChange={handleChange}
          disabled={isSwitching || !hasSwitchPermission}
          aria-label={t('tenantSwitcher.selector.label')}
          aria-disabled={!hasSwitchPermission}
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-background)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family-sans)',
            minWidth: 200,
            cursor: !hasSwitchPermission || isSwitching ? 'not-allowed' : 'pointer',
            opacity: !hasSwitchPermission || isSwitching ? 0.6 : 1,
          }}
        >
          {tenants?.map((t_) => (
            <option key={t_.tenantId} value={t_.tenantId}>
              {t_.label} ({t_.tenantId}) — {t_.sampleCount}건
            </option>
          ))}
        </select>
      )}

      {/* 전환 중 스피너 */}
      {isSwitching && (
        <span
          aria-live="polite"
          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}
        >
          ●●●
        </span>
      )}

      {/* 해석 출처 배지 */}
      {context?.resolvedFrom && !isLoading && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('tenantSwitcher.selector.resolvedFrom')}:{' '}
          <strong style={{ color: 'var(--color-brand-primary)' }}>
            {t(resolvedFromKey, { defaultValue: context.resolvedFrom })}
          </strong>
        </span>
      )}

      {/* 권한 없음 안내 */}
      {!hasSwitchPermission && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-warning)',
          }}
        >
          ({t('tenantSwitcher.error.TENANT-403-001')})
        </span>
      )}
    </div>
  );
}
